import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {tap} from 'rxjs/operators';
import {SystemUser, SystemUserSimple} from '../../models/system-user.model';
import {FormState} from '../../models/form-state.model';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {PartnerState} from '../partner/partner.state';
import {UserService} from '../../services/user.service';
import {Injectable} from '@angular/core';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {UserActions} from './user.actions';
import {NotificationService} from '../../services/notification.service';
import {DictionaryItem} from '../../models/dictionary.model';


interface UserStateModel {
    form: FormState;
    list: SearchList<SystemUser>;
    sellerList: SystemUserSimple[];
    activeUser: SystemUser;
    roleFieldList: DictionaryItem[];
}

const STATE_NAME = 'user';

@State<UserStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<SystemUser>({
            filters: new FormState(),
        }),
        sellerList: [],
        activeUser: null,
        roleFieldList: null,
    }
})

@Injectable()
export class UserState {
    constructor(
        private store: Store,
        private navService: NavService,
        private userService: UserService,
        private notificationService: NotificationService,
    ) {
    }

    @Selector()
    static activeUser(state) {
        return state.activeUser;
    }

    @Selector()
    static roleFieldList(state) {
        return state.roleFieldList;
    }

    @Selector()
    static userList(state) {
        return state.list;
    }

    @Selector()
    static userFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static sellerList(state) {
        return state.sellerList;
    }

    @Action(UserActions.GetListPage)
    getListPage(ctx: StateContext<UserStateModel>, {localizationId, params}: UserActions.GetListPage) {
        const partner = this.store.selectSnapshot(PartnerState.activePartner);
        return handleFetchListPage(ctx, params, props => this.userService.fetchList(partner.id, props, localizationId));
    }

    @Action(UserActions.GetById)
    getUser(ctx: StateContext<UserStateModel>, {userId}: UserActions.GetById) {
        return this.userService.getById(userId).pipe(
            tap((user) => {
                ctx.patchState({
                    activeUser: new SystemUser(user)
                });
            })
        );
    }

    @Action(UserActions.Activate)
    activate(
        ctx: StateContext<UserStateModel>,
        {partnerId, userId}: UserActions.Activate
    ) {
        return this.userService.activate(userId).subscribe(
            () => ctx.dispatch(new UserActions.GetListPage(partnerId, null))
        );
    }

    @Action(UserActions.Deactivate)
    deactivate(
        ctx: StateContext<UserStateModel>,
        {partnerId, userId}: UserActions.Deactivate
    ) {
        return this.userService.deactivate(userId).subscribe(
            () => ctx.dispatch(new UserActions.GetListPage(partnerId, null))
        );
    }

    @Action(UserActions.PassReset)
    passReset(
        ctx: StateContext<UserStateModel>,
        {partnerId, userId}: UserActions.PassReset
    ) {
        return this.userService.passReset(userId).subscribe(
            () => {
                this.notificationService.success('LABELS.PASSWORD_RESET_NOTIFICATION');
                ctx.dispatch(new UserActions.GetListPage(partnerId, null));
            }
        );
    }

    @Action(UserActions.Add)
    addUser(ctx: StateContext<UserStateModel>, {user}: UserActions.Add) {
        const partner = this.store.selectSnapshot(PartnerState.activePartner);
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.userService.add(user).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.USER_MANAGEMENT, {partnerId: partner.id}),
            ({error}) => ctx.dispatch(new UserActions.FormFailure(error))
        );
    }

    @Action(UserActions.Update)
    updateUser(ctx: StateContext<UserStateModel>, {user}: UserActions.Update) {
        const partner = this.store.selectSnapshot(PartnerState.activePartner);
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.userService.update(user).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.USER_MANAGEMENT, {partnerId: partner.id}),
            ({error}) => ctx.dispatch(new UserActions.FormFailure(error))
        );
    }

    @Action(UserActions.FormFailure)
    addUserFailure(ctx: StateContext<UserStateModel>, {validationErrors = []}: UserActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(UserActions.Clear)
    userClear(ctx: StateContext<UserStateModel>) {
        ctx.patchState({
            activeUser: null
        });
    }

    @Action(UserActions.ListClear)
    userListClear(ctx: StateContext<UserStateModel>) {
        ctx.patchState({
            list: new SearchList<SystemUser>({
                filters: new FormState(),
            }),
        });
    }

    @Action(UserActions.GetRoleFieldList)
    getRoleFieldList(ctx: StateContext<UserStateModel>, {partnerId}: UserActions.GetRoleFieldList) {
        return this.userService.getRoleFieldList(partnerId).subscribe(roleFieldList => ctx.patchState({
            roleFieldList
        }));
    }

    @Action(UserActions.SellerList)
    getFieldList(ctx: StateContext<UserStateModel>) {
        return this.userService.getSellerList().pipe(
            tap((sellerList) => ctx.patchState({
                sellerList: sellerList.map(seller => new SystemUserSimple(seller))
            }))
        );
    }
}
